import React, { useEffect, useRef, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { getProjectList } from "../../Redux/Actions/projectActions";

const ProjectReportDropdown = ({ value, onChange, selectedName }) => {
    const dispatch = useDispatch();
    const [projects, setProjects] = useState([]);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [selectedLabel, setSelectedLabel] = useState("Select Project");

    // Sync label when editing an existing record
    useEffect(() => {
        if (selectedName) setSelectedLabel(selectedName);
    }, [selectedName]);
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const listRef = useRef(null);
    const searchRef = useRef(null);
    const hasMoreRef = useRef(true);
    const pageRef = useRef(1);
    const wrapperRef = useRef(null);

    // Debounce search input
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(search);
        }, 400);
        return () => clearTimeout(handler);
    }, [search]);

    const fetchProjects = useCallback(async (pageNum, searchTerm = "") => {
        if (loading || (!hasMoreRef.current && pageNum > 1)) return;
        setLoading(true);
        try {
            const response = await dispatch(getProjectList({
                page: pageNum,
                limit: 20,
                is_deleted: false,
                business_type: "fresh",
                status: ["published"],
                ...(searchTerm && { filter: searchTerm }),
            }, false));

            const data = response?.data?.data || response?.data || [];
            const list = Array.isArray(data) ? data : [];

            setProjects((prev) => pageNum === 1 ? list : [...prev, ...list]);

            if (list.length < 20) {
                hasMoreRef.current = false;
                setHasMore(false);
            } else {
                hasMoreRef.current = true;
                setHasMore(true);
            }
        } catch {
            // silent
        } finally {
            setLoading(false);
        }
    }, [dispatch, loading]);

    // Reset and refetch when search changes
    useEffect(() => {
        hasMoreRef.current = true;
        pageRef.current = 1;
        setProjects([]);
        fetchProjects(1, debouncedSearch);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearch]);

    // Initial load
    useEffect(() => {
        fetchProjects(1, "");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Close on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Focus search input when dropdown opens
    useEffect(() => {
        if (isOpen && searchRef.current) {
            setTimeout(() => searchRef.current?.focus(), 50);
        }
        if (!isOpen) {
            setSearch("");
        }
    }, [isOpen]);

    const handleScroll = () => {
        const el = listRef.current;
        if (!el || loading || !hasMoreRef.current) return;
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 10) {
            const nextPage = pageRef.current + 1;
            pageRef.current = nextPage;
            fetchProjects(nextPage, debouncedSearch);
        }
    };

    const handleSelect = (project) => {
        setSelectedLabel(project.project_name);
        onChange({ target: { value: project._id } });
        setIsOpen(false);
    };

    const handleClear = (e) => {
        e.stopPropagation();
        setSelectedLabel("Select Project");
        onChange({ target: { value: "" } });
    };

    return (
        <div ref={wrapperRef} style={{ position: "relative", width: "100%" }}>
            {/* Trigger */}
            <div
                onClick={() => setIsOpen((prev) => !prev)}
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    height: 42,
                    borderRadius: 10,
                    border: `1.5px solid ${isOpen ? "#0D1A52" : "#e5e7eb"}`,
                    padding: "0 14px",
                    fontSize: 14,
                    background: "#f9fafb",
                    cursor: "pointer",
                    userSelect: "none",
                    boxShadow: isOpen ? "0 0 0 3px rgba(13,26,82,0.1)" : "none",
                    transition: "border-color 0.2s, box-shadow 0.2s",
                }}
            >
                <span style={{ color: value ? "#111827" : "#c0c4cc" }}>
                    {selectedLabel}
                </span>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    {value && (
                        <span
                            onClick={handleClear}
                            style={{ fontSize: 12, color: "#9ca3af", cursor: "pointer", lineHeight: 1 }}
                        >
                            ✕
                        </span>
                    )}
                    <svg
                        width="14" height="14" viewBox="0 0 24 24" fill="none"
                        stroke="#6b7280" strokeWidth="2.5" strokeLinecap="round"
                        style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}
                    >
                        <polyline points="6 9 12 15 18 9" />
                    </svg>
                </div>
            </div>

            {/* Dropdown */}
            {isOpen && (
                <div
                    style={{
                        position: "absolute",
                        top: "calc(100% + 6px)",
                        left: 0,
                        right: 0,
                        background: "white",
                        border: "1.5px solid #e5e7eb",
                        borderRadius: 10,
                        boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                        zIndex: 1000,
                        overflow: "hidden",
                    }}
                >
                    {/* Search Input */}
                    <div style={{
                        padding: "8px 10px",
                        borderBottom: "1px solid #f3f4f6",
                        position: "sticky",
                        top: 0,
                        background: "white",
                        zIndex: 1,
                    }}>
                        <div style={{ position: "relative" }}>
                            <svg
                                width="14" height="14" viewBox="0 0 24 24" fill="none"
                                stroke="#9ca3af" strokeWidth="2.5" strokeLinecap="round"
                                style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}
                            >
                                <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                            </svg>
                            <input
                                ref={searchRef}
                                type="text"
                                placeholder="Search projects…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                    width: "100%",
                                    height: 34,
                                    borderRadius: 8,
                                    border: "1.5px solid #e5e7eb",
                                    paddingLeft: 32,
                                    paddingRight: search ? 30 : 10,
                                    fontSize: 13,
                                    background: "#f9fafb",
                                    outline: "none",
                                    boxSizing: "border-box",
                                    color: "#111827",
                                    transition: "border-color 0.2s",
                                }}
                                onFocus={(e) => e.target.style.borderColor = "#0D1A52"}
                                onBlur={(e) => e.target.style.borderColor = "#e5e7eb"}
                            />
                            {search && (
                                <span
                                    onClick={(e) => { e.stopPropagation(); setSearch(""); }}
                                    style={{
                                        position: "absolute", right: 10, top: "50%",
                                        transform: "translateY(-50%)", fontSize: 12,
                                        color: "#9ca3af", cursor: "pointer",
                                    }}
                                >
                                    ✕
                                </span>
                            )}
                        </div>
                    </div>

                    {/* List */}
                    <div
                        ref={listRef}
                        onScroll={handleScroll}
                        style={{ maxHeight: 200, overflowY: "auto" }}
                    >
                        {projects.length === 0 && !loading && (
                            <div style={{ padding: "12px 14px", color: "#9ca3af", fontSize: 13 }}>
                                {search ? `No projects found for "${search}"` : "No projects found"}
                            </div>
                        )}

                        {projects.map((p) => (
                            <div
                                key={p._id}
                                onClick={() => handleSelect(p)}
                                style={{
                                    padding: "10px 14px",
                                    fontSize: 14,
                                    cursor: "pointer",
                                    background: value === p._id ? "#f0f4ff" : "white",
                                    color: value === p._id ? "#0D1A52" : "#111827",
                                    fontWeight: value === p._id ? 600 : 400,
                                    borderBottom: "1px solid #f3f4f6",
                                    transition: "background 0.15s",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 8,
                                }}
                                onMouseEnter={(e) => { if (value !== p._id) e.currentTarget.style.background = "#f9fafb"; }}
                                onMouseLeave={(e) => { if (value !== p._id) e.currentTarget.style.background = "white"; }}
                            >
                                <span>{p.project_name}</span>
                                {p.business_type && (
                                    <span style={{
                                        fontSize: 11,
                                        fontWeight: 600,
                                        padding: "2px 8px",
                                        borderRadius: 20,
                                        flexShrink: 0,
                                        background:
                                            p.business_type === "fresh" ? "#f0f4ff" :
                                                p.business_type === "resale" ? "#fff7e6" :
                                                    p.business_type === "rental" ? "#f6ffed" :
                                                        p.business_type === "business" ? "#fff0f6" : "#f3f4f6",
                                        color:
                                            p.business_type === "fresh" ? "#0D1A52" :
                                                p.business_type === "resale" ? "#d46b08" :
                                                    p.business_type === "rental" ? "#389e0d" :
                                                        p.business_type === "business" ? "#c41d7f" : "#6b7280",
                                    }}>
                                        {p.business_type.charAt(0).toUpperCase() + p.business_type.slice(1)}
                                    </span>
                                )}
                            </div>
                        ))}

                        {loading && (
                            <div style={{ padding: "10px 14px", fontSize: 13, color: "#9ca3af", textAlign: "center" }}>
                                Loading...
                            </div>
                        )}

                        {!hasMore && projects.length > 0 && (
                            <div style={{ padding: "8px 14px", fontSize: 12, color: "#d1d5db", textAlign: "center" }}>
                                All projects loaded
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProjectReportDropdown;